import {
  Distribution,
  Cradle,
  DigitalPlatform,
  DistributionUserData,
  MusicalReleaseStatus,
  MusicalRelease,
  DistributionStatusI,
} from '@solo/app-core';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useCradle } from '@/app/contexts';
import { translate } from '@/app/i18n';
import Colors from '@/app/styles/Colors';
import { Section, Flex, Text, Button } from '@/app/ui/atoms';
import {
  CheckboxWithLabel,
  InputDateWithLabel,
  InputWithLabel,
  Modal,
  MusicalReleaseDistributionForm,
  TimePickerWithLabel,
} from '@/app/ui';
import { useBlocState } from '@/app/hooks';
import { MusicalReleaseDistributionData } from '@/app/ui/organisms/MusicalReleaseDistributionForm/MusicalReleaseDistributionForm';
import dayjs from '@/app/utils/dayjs';
import { stringToInt } from '@/app/utils/StringToInt';
import Icons from '@/app/ui/molecules/Icons';

interface DigitalDistribution {
  distribution: Distribution;
  digitalPlatform?: DigitalPlatform;
}

const DigitalDistributionComponent = () => {
  const { id } = useParams();
  const { digitalDistributionBloc, authBloc, userBloc, musicalWorksBloc } = useCradle<Cradle>();
  const authState = useBlocState(authBloc);
  const digitalDistributionBlocState = useBlocState(digitalDistributionBloc);
  const [digitalDistributions, setDigitalDistributions] = useState<DigitalDistribution[]>([]);
  const [editDistribution, setEditDistribution] = useState<boolean>(false);
  const [releaseDate, setReleaseDate] = useState<Date | undefined>(dayjs().toDate());
  const [releaseTime, setReleaseTime] = useState<string | undefined>(dayjs().toISOString());
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [userDistributionData, setUserDistributionData] = useState<Partial<DistributionUserData>>();
  const [musicalRelease, setMusicalRelease] = useState<MusicalRelease | undefined>(undefined);
  const [appleId, setAppleId] = useState<string | undefined>(undefined);
  const [spotifyId, setSpotifyId] = useState<string | undefined>(undefined);
  const [digitalPlatforms, setDigitalPlatforms] = useState<DigitalPlatform[]>([]);
  const [digitalPlatformMap, setDigitalPlatformMap] = useState<Map<number, DigitalPlatform>>(
    new Map(),
  );
  const [validationLoading, setValidationLoading] = useState<boolean>(false);
  const [checkedStores, setCheckedStores] = useState<number[]>([]);
  const [distributionStatuses, setDistributionStatuses] = useState<DistributionStatusI[]>([]);

  const validateAppleAndSpotify = (appleIdParam?: string, spotifyIdParam?: string) => {
    if (!appleIdParam || !spotifyIdParam) return false;
    const applePlatform = digitalPlatforms.find((p) => p.name.includes('Apple Music'));
    const spotifyPlatform = digitalPlatforms.find((p) => p.name.includes('Spotify'));
    return (
      (applePlatform && checkedStores.find((pId) => pId === applePlatform?.id) && appleIdParam) ||
      (spotifyPlatform &&
        checkedStores.find((pId) => pId === spotifyPlatform?.id) &&
        spotifyIdParam)
    );
  };

  const validationErrors = (distributionUserData: Partial<DistributionUserData>) =>
    !!releaseDate &&
    !!releaseTime &&
    validateAppleAndSpotify(distributionUserData.appleId, distributionUserData.spotifyId);

  const distribute = async () => {
    if (authState.kind !== 'AuthenticatedAuthState' || !authState.user.id) return;

    let distributionUserData: Partial<DistributionUserData> | undefined = userDistributionData;

    if (validateAppleAndSpotify(appleId, spotifyId)) {
      if (userDistributionData) {
        distributionUserData = await userBloc.updateDistributionUserData({
          appleId: appleId || '',
          spotifyId: spotifyId || '',
          artistName: musicalRelease?.artistName || '',
          userId: authState.user.id,
        });
      } else {
        distributionUserData = await userBloc.createDistributionUserData({
          appleId: appleId || '',
          spotifyId: spotifyId || '',
          artistName: musicalRelease?.artistName || '',
          userId: authState.user.id,
        });
      }
    }

    setShowErrors(true);
    if (
      !distributionUserData ||
      (musicalRelease?.status !== MusicalReleaseStatus.VALIDATED &&
        !validationErrors(distributionUserData))
    ) {
      toast.error(translate('TOAST.VALIDATION_ERROR'));
      return;
    }

    if (!id || !releaseTime) return;

    try {
      await musicalWorksBloc.distributeMusicalRelease(id, {
        storeIds: checkedStores.map((storeId) => storeId),
        saleStartDate: dayjs(releaseDate).format('YYYY-MM-DD'),
        saleStartTime: releaseTime,
      });
    } catch (error) {
      toast.error(translate('TOAST.VALIDATION_ERROR'));
    }
  };

  async function loadDistribution() {
    if (digitalDistributionBlocState.kind !== 'LoadedDigitalDistributionState' || !id) return;
    const distributions = await digitalDistributionBloc.getDistributions({
      musicalReleaseId: id,
    });
    const dist: DigitalDistribution[] = distributions.map((distribution) => {
      const digitalPlatform = digitalDistributionBlocState.platforms.find(
        (p) => p.id === distribution.digitalPlatformId,
      );
      return { distribution, digitalPlatform };
    });
    setDigitalDistributions(dist);
  }

  async function saveDistributions(updatedDistributions: MusicalReleaseDistributionData) {
    if (!digitalDistributions || !id) return;
    updatedDistributions.newDistributions.forEach(async (distribution) => {
      await digitalDistributionBloc.createDistribution(id, distribution);
    });
    updatedDistributions.updatedDistributions.forEach(async (distribution) => {
      await digitalDistributionBloc.updateDistribution(
        distribution.distributionId,
        distribution.props,
      );
    });
    updatedDistributions.deletedDistributions.forEach(async (deletedId) => {
      await digitalDistributionBloc.deleteDistribution(deletedId);
    });

    loadDistribution();
  }

  const getUserDistributionData = async () => {
    if (authState.kind !== 'AuthenticatedAuthState' || !authState.user.id) return;
    const distributionUserData = await userBloc.getMeDistributionUserData();
    if (distributionUserData) {
      setUserDistributionData(distributionUserData);
    }
  };

  const getMusicalRelease = async () => {
    if (!id) return;
    const musicalWork = await musicalWorksBloc.getMusicalRelease(id);
    setMusicalRelease(musicalWork);
  };

  const validate = async () => {
    if (!id) return;
    try {
      setValidationLoading(true);
      await musicalWorksBloc.validateMusicalRelease(id);
      getMusicalRelease();
    } catch (error) {
      toast.error(translate('TOAST.VALIDATION_ERROR'));
    } finally {
      setValidationLoading(false);
    }
  };

  const getDigitalPlatforms = async () => {
    try {
      const stores = await digitalDistributionBloc.getDigitalPlatforms();
      const storesActivated = stores.filter((store) => store.isActive);
      setDigitalPlatforms(storesActivated);
      const digitalPlatformMapResult = new Map<number, DigitalPlatform>();
      storesActivated.forEach((platform) => {
        digitalPlatformMapResult.set(platform.id, platform);
      });
      setDigitalPlatformMap(digitalPlatformMapResult);
    } catch (error) {
      toast.error(translate('TOAST.LOAD_ERROR'));
    }
  };

  const toggleAll = () => {
    if (checkedStores && checkedStores.length === digitalPlatforms.length) {
      setCheckedStores([]);
    } else {
      setCheckedStores(digitalPlatforms.map((platform) => platform.id));
    }
  };

  const checkStore = (store: number | undefined) => {
    if (store) {
      if (!checkedStores.includes(store)) {
        setCheckedStores([...checkedStores, store]);
        return;
      }
      setCheckedStores(checkedStores.filter((checkedStore) => checkedStore !== store));
    }
  };

  const isAppleChecked = (store: DigitalPlatform) =>
    checkedStores.includes(store.id) && store.name.includes('Apple Music');

  const isSpotifyChecked = (store: DigitalPlatform) =>
    checkedStores.includes(store.id) && store.name.includes('Spotify');

  useEffect(() => {
    getUserDistributionData();
    getMusicalRelease();
    getDigitalPlatforms();
  }, [authState.kind]);

  useEffect(() => {
    if (musicalRelease?.distributionStatuses && digitalPlatformMap.size > 0) {
      const distributionStatusesResult = musicalRelease.distributionStatuses.map((status) => ({
        ...status,
        distributorStoreName: digitalPlatformMap.get(Number(status.distributorStoreId))?.name || '',
      }));
      setDistributionStatuses(distributionStatusesResult);
    }
  }, [musicalRelease, digitalPlatformMap]);

  async function validateEmail() {
    if (authState.kind !== 'AuthenticatedAuthState' || !authState.user.id) return;
    try {
      await userBloc.validateEmailRequest();
      toast.success(translate('TOAST.EMAIL_REQUESTED'));
    } catch (error) {
      toast.error(translate('TOAST.EMAIL_VALIDATION_ERROR'));
    }
  }

  useEffect(() => {
    loadDistribution();
  }, []);

  return (
    <Section>
      <Flex.Container flex={1} gap="3rem" flexDirection="column" style={{ maxWidth: '1000px' }}>
        <Flex.Container flex={1} gap="3rem" flexDirection="column">
          {authState.kind === 'AuthenticatedAuthState' && !authState.user.emailVerifiedAt && (
            <Flex.Container gap="10px" alignItems="center">
              <Icons.AlertCircleIcon />
              <Text.Paragraph>{translate('PROFILE_INFO.VALIDATE_EMAIL')}</Text.Paragraph>
              <Button $variant="primary" $transparent onClick={() => validateEmail()}>
                <Text.Span fontWeight={700}>
                  {translate('PROFILE_INFO.VALIDATE_EMAIL_BUTTON')}
                </Text.Span>
              </Button>
            </Flex.Container>
          )}
          <Flex.Container gap="3rem" justifyContent="space-between" alignItems="center">
            <Flex.Container style={{ paddingTop: '2px' }} alignItems="center" gap="10px">
              <Text.SubtitleBold>
                {translate('DIGITAL_DISTRIBUTION.VALIDATION_STATUS')}
              </Text.SubtitleBold>
              {musicalRelease?.status && (
                <Text.Paragraph
                  style={{ padding: 0 }}
                  fontWeight={600}
                  color={
                    musicalRelease?.status !== 'VALIDATED' &&
                    musicalRelease?.status !== 'DISTRIBUTING' &&
                    musicalRelease?.status !== 'DISTRIBUTED'
                      ? Colors.red
                      : Colors.greenSolid
                  }
                >
                  {translate(`DIGITAL_DISTRIBUTION.ENUM_STATUS.${musicalRelease?.status}`)}
                </Text.Paragraph>
              )}
            </Flex.Container>
            {musicalRelease?.status !== 'DISTRIBUTING' && musicalRelease?.status !== 'DISTRIBUTED' && (
              <Flex.Container flex={1} style={{ paddingTop: '2px' }}>
                <Button
                  $size="md"
                  $variant="primary"
                  onClick={validate}
                  $disabled={validationLoading}
                >
                  {validationLoading ? (
                    <Icons.Spinner style={{ color: Colors.primary }} />
                  ) : (
                    <Text.Paragraph style={{ padding: 0 }} fontWeight={600}>
                      {translate('BUTTON.VALIDATE')}
                    </Text.Paragraph>
                  )}
                </Button>
              </Flex.Container>
            )}
          </Flex.Container>
          {musicalRelease?.validationErrors && musicalRelease.validationErrors.length > 0 && (
            <Flex.Container flexDirection="column" gap="1rem">
              <Text.SubtitleBold>
                {translate('DIGITAL_DISTRIBUTION.VALIDATION_ERRORS')}
              </Text.SubtitleBold>
              <Flex.Container flexDirection="column" gap="0.5rem">
                {musicalRelease.validationErrors.map((error, index) => (
                  <Flex.Container
                    key={`${error.objectId}-${error.errorCode}-${index.toString()}`}
                    gap="0.5rem"
                    alignItems="center"
                  >
                    <Icons.AlertCircleIcon />
                    <Text.Paragraph>{error.errorMessage}</Text.Paragraph>
                  </Flex.Container>
                ))}
              </Flex.Container>
            </Flex.Container>
          )}
          {musicalRelease?.status === MusicalReleaseStatus.VALIDATED && (
            <Flex.Container gap="3rem" flexDirection="column">
              <Flex.Item flex={2} style={{ paddingTop: '2px' }}>
                <Text.SubtitleBold>
                  {translate('DIGITAL_DISTRIBUTION.DISTRIBUTION')}
                </Text.SubtitleBold>
              </Flex.Item>
              <Flex.Container style={{ paddingTop: '2px' }}>
                <Flex.Item flex={2}>
                  <Flex.Container flexDirection="column" gap="20px">
                    <CheckboxWithLabel
                      id="all"
                      value="all"
                      checked={
                        checkedStores !== undefined &&
                        checkedStores.length === digitalPlatforms.length
                      }
                      onChange={() => toggleAll()}
                      label={translate('WORKS_PAGES.SELECT_ALL_STORES')}
                    />
                    <Flex.Container flexDirection="row" flexWrap="wrap" gap="20px 0px">
                      {digitalPlatforms.map((store) => (
                        <Flex.Container
                          key={store.id}
                          flexDirection="column"
                          style={{ width: '33%', paddingTop: '2px' }}
                        >
                          <CheckboxWithLabel
                            id={store.id.toString()}
                            value={store.id.toString()}
                            onChange={(value?: string) => value && checkStore(stringToInt(value))}
                            checked={checkedStores.includes(store.id)}
                            label={store.name}
                          />
                          {isAppleChecked(store) && (
                            <Flex.Item
                              style={{
                                maxWidth: '300px',
                                paddingTop: '1rem',
                                paddingLeft: '3.5rem',
                              }}
                            >
                              <InputWithLabel
                                label={`${translate('FORMS.APPLE_ID')}`}
                                placeholder={translate('FORMS.APPLE_ID')}
                                value={userDistributionData?.appleId || appleId}
                                onChange={(e) => setAppleId(e)}
                              />
                            </Flex.Item>
                          )}
                          {isSpotifyChecked(store) && (
                            <Flex.Item
                              style={{
                                maxWidth: '300px',
                                paddingTop: '1rem',
                                paddingLeft: '3.5rem',
                              }}
                            >
                              <InputWithLabel
                                label={`${translate('FORMS.SPOTIFY_ID')}`}
                                placeholder={translate('FORMS.SPOTIFY_ID')}
                                value={userDistributionData?.spotifyId || spotifyId}
                                onChange={(e) => setSpotifyId(e)}
                              />
                            </Flex.Item>
                          )}
                        </Flex.Container>
                      ))}
                    </Flex.Container>
                  </Flex.Container>
                </Flex.Item>
              </Flex.Container>
              <Flex.Container style={{ paddingTop: '2px' }}>
                <Flex.Item flex={1}>
                  <Flex.Container style={{ maxWidth: '300px' }}>
                    <InputDateWithLabel
                      defaultValue={releaseDate}
                      label={translate('DIGITAL_DISTRIBUTION.SALE_START_DATE')}
                      onChange={(r) => setReleaseDate(r)}
                      mandatoryError={showErrors && !releaseDate}
                    />
                  </Flex.Container>
                </Flex.Item>
                <Flex.Item flex={1}>
                  <Flex.Container style={{ maxWidth: '300px' }}>
                    <TimePickerWithLabel
                      defaultValue={releaseTime ? dayjs(releaseTime) : undefined}
                      label={translate('DIGITAL_DISTRIBUTION.SALE_START_TIME')}
                      onChange={(time) => {
                        setReleaseTime(time);
                      }}
                      mandatoryError={showErrors && !releaseDate}
                    />
                  </Flex.Container>
                </Flex.Item>
              </Flex.Container>
              <Flex.Item flex={1} style={{ maxWidth: '300px' }}>
                <Button
                  $size="md"
                  $variant="primary"
                  onClick={distribute}
                  $disabled={musicalRelease?.status !== MusicalReleaseStatus.VALIDATED}
                >
                  {translate('DIGITAL_DISTRIBUTION.DISTRIBUTE')}
                </Button>
              </Flex.Item>
            </Flex.Container>
          )}
        </Flex.Container>

        {
          // (musicalRelease?.status === MusicalReleaseStatus.DISTRIBUTING ||
          // musicalRelease?.status === MusicalReleaseStatus.DISTRIBUTED) &&
          musicalRelease?.distributionStatuses && musicalRelease?.distributionStatuses.length > 0 && (
            <Flex.Container flex={1} className="table" flexDirection="column" gap="3rem">
              <Flex.Container gap="3rem">
                <Text.BiggerBold>{translate('DIGITAL_DISTRIBUTION.PLATFORM')}</Text.BiggerBold>
                {/* <Button $transparent $variant="primary" onClick={() => setEditDistribution(true)}>
                  <Text.SectionSubtitle>
                    {translate('WORKS_PAGES.EDIT_DISTRIBUTION')}
                  </Text.SectionSubtitle>
                </Button> */}
              </Flex.Container>
              <Flex.Container flexDirection="column">
                <Flex.Container flex={1} className="thead">
                  <Flex.Container flex={3} className="th" alignItems="center">
                    {/* <Input.Checkbox
                      style={{ marginRight: 15 }}
                      onChange={() => checkAllPlatforms()}
                    /> */}
                    <Text.Paragraph fontWeight={600} style={{ padding: 0 }}>
                      {translate('DIGITAL_DISTRIBUTION.PLATFORM')}
                    </Text.Paragraph>
                  </Flex.Container>
                  <Flex.Item flex={4} className="th">
                    {translate('DIGITAL_DISTRIBUTION.STATUS')}
                  </Flex.Item>
                  {/* <Flex.Item flex={2} className="th">
                    {translate('WORKS.RELEASED_AT')}
                  </Flex.Item>
                  <Flex.Item flex={2} className="th">
                    {translate('ACTIONS.ACTIONS')}
                  </Flex.Item> */}
                </Flex.Container>
                <Flex.Container className="tbody" flexDirection="column">
                  {distributionStatuses?.map((digitalDs) => (
                    <Flex.Container
                      key={digitalDs.distributorStoreId}
                      className="tr"
                      alignItems="center"
                    >
                      <Flex.Container
                        className="td"
                        flex={3}
                        alignItems="center"
                        style={{ paddingLeft: 10 }}
                      >
                        {/* <Input.Checkbox
                          style={{ marginRight: 15 }}
                          onChange={() => checkPlatform(digitalDs.digitalPlatform?.id)}
                          checked={
                            digitalDs.digitalPlatform
                              ? selectedPlatforms.includes(digitalDs.digitalPlatform.id)
                              : false
                          }
                        /> */}
                        {/* <DigitalDistributionIcons
                        style={{ marginRight: 15 }}
                        width={25}
                        icon={digitalDs.digitalPlatform?.name || ''} // TODO icon not found
                      /> */}
                        {digitalDs.distributorStoreName || 'Platform not found'}
                      </Flex.Container>
                      <Flex.Item flex={4} className="td">
                        <Text.LabelMedium style={{ textTransform: 'capitalize' }}>
                          {translate(`DISTRIBUTION_STATUS.${digitalDs.status}`)}
                        </Text.LabelMedium>
                      </Flex.Item>
                      {/* <Flex.Item flex={2} className="td">
                        <Text.LabelMedium style={{ textTransform: 'capitalize' }}>
                          00/00/0000
                        </Text.LabelMedium>
                      </Flex.Item>
                      <Flex.Item flex={2} className="td">
                        <Text.Paragraph fontWeight={600} color={Colors.primary}>
                          Release
                        </Text.Paragraph>
                      </Flex.Item> */}
                    </Flex.Container>
                  ))}
                </Flex.Container>
              </Flex.Container>
            </Flex.Container>
          )
        }
      </Flex.Container>
      <Modal
        isOpen={editDistribution}
        onClose={() => setEditDistribution(false)}
        closeable
        position="top"
        size="lg"
      >
        {id && (
          <Flex.Container flexDirection="column" gap="40px" padding="30px 90px 20px 90px">
            <MusicalReleaseDistributionForm
              musicalReleaseId={id}
              distributionData={digitalDistributions.map((dd) => dd.distribution)}
              closeModal={() => setEditDistribution(false)}
              saveDistributions={(distribution) => saveDistributions(distribution)}
            />
          </Flex.Container>
        )}
      </Modal>
    </Section>
  );
};

export default DigitalDistributionComponent;
